import { useEffect, useState } from 'react';
import AscendingK from '../components/AscendingK';
import { Icons } from '../components/Icons';
import { apiRequest } from '../lib/api';

const TOKEN_KEY = 'connektedin_token';

function decodeJwtPayload(token) {
  const payloadPart = String(token || '').split('.')[1] || '';
  if (!payloadPart) {
    return null;
  }

  const normalized = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
  return JSON.parse(atob(padded));
}

function readAdminRole() {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      return null;
    }
    const payload = decodeJwtPayload(token);
    return payload?.role || null;
  } catch {
    return null;
  }
}

export function isAdminAuthenticated() {
  return readAdminRole() === 'admin';
}

export function adminLogout() {
  localStorage.removeItem(TOKEN_KEY);
}

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAdminAuthenticated()) {
      window.location.replace('/admin');
    }
  }, []);

  const handleLogin = async (event) => {
    event.preventDefault();

    setLoading(true);
    setError('');

    try {
      const data = await apiRequest('/auth/login', {
        method: 'POST',
        body: { email, password },
        token: null
      });

      if (data.user?.role !== 'admin') {
        localStorage.removeItem(TOKEN_KEY);
        setError('This account does not have admin access.');
        return;
      }

      localStorage.setItem(TOKEN_KEY, data.token);
      window.location.replace('/admin');
    } catch (requestError) {
      setError(requestError.message || 'Invalid credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#1A1A2E',
        padding: 24
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 400,
          background: '#0F0F1A',
          borderRadius: 12,
          padding: '40px 32px',
          border: '1px solid rgba(201, 169, 97, 0.15)',
          boxShadow: '0 24px 48px rgba(0,0,0,0.4)'
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 16 }}>
            <AscendingK size={28} color="#C9A961" />
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 18, fontWeight: 600, color: '#F5F2EB' }}>
              Admin Panel
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 8 }}>
            {Icons.shield}
            <span
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10,
                color: 'rgba(245,242,235,0.4)',
                letterSpacing: 1.5,
                textTransform: 'uppercase'
              }}
            >
              Restricted Access
            </span>
          </div>
          <p style={{ fontSize: 13, color: 'rgba(245,242,235,0.45)', margin: 0 }}>
            Server-backed admin sign-in for platform operators only.
          </p>
        </div>

        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: 16 }}>
            <label
              style={{
                display: 'block',
                fontSize: 12,
                fontWeight: 600,
                color: 'rgba(245,242,235,0.6)',
                marginBottom: 6,
                fontFamily: "'JetBrains Mono', monospace",
                textTransform: 'uppercase',
                letterSpacing: 0.5
              }}
            >
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              spellCheck={false}
              style={{
                width: '100%',
                padding: '12px 14px',
                fontSize: 14,
                background: 'rgba(245,242,235,0.04)',
                border: '1px solid rgba(245,242,235,0.1)',
                borderRadius: 6,
                color: '#F5F2EB',
                outline: 'none',
                fontFamily: "'DM Sans', sans-serif",
                boxSizing: 'border-box'
              }}
              placeholder="admin@connektedin.com"
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label
              style={{
                display: 'block',
                fontSize: 12,
                fontWeight: 600,
                color: 'rgba(245,242,235,0.6)',
                marginBottom: 6,
                fontFamily: "'JetBrains Mono', monospace",
                textTransform: 'uppercase',
                letterSpacing: 0.5
              }}
            >
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                autoComplete="current-password"
                autoCapitalize="none"
                spellCheck={false}
                style={{
                  width: '100%',
                  padding: '12px 44px 12px 14px',
                  fontSize: 14,
                  background: 'rgba(245,242,235,0.04)',
                  border: '1px solid rgba(245,242,235,0.1)',
                  borderRadius: 6,
                  color: '#F5F2EB',
                  outline: 'none',
                  fontFamily: "'DM Sans', sans-serif",
                  boxSizing: 'border-box'
                }}
                placeholder="Enter password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((value) => !value)}
                style={{
                  position: 'absolute',
                  right: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: 'rgba(245,242,235,0.4)',
                  cursor: 'pointer',
                  padding: 0
                }}
              >
                {Icons.eye}
              </button>
            </div>
          </div>

          {error && (
            <div
              style={{
                background: 'rgba(220, 53, 69, 0.1)',
                border: '1px solid rgba(220, 53, 69, 0.2)',
                borderRadius: 6,
                padding: '10px 14px',
                fontSize: 13,
                color: '#DC3545',
                marginBottom: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              {Icons.warning} {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !email || !password}
            style={{
              width: '100%',
              padding: '14px 20px',
              background: loading ? 'rgba(201, 169, 97, 0.5)' : '#C9A961',
              color: '#1A1A2E',
              border: 'none',
              borderRadius: 6,
              fontSize: 15,
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: "'DM Sans', sans-serif"
            }}
          >
            {loading ? 'Authenticating...' : 'Access Admin Panel'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: 24, paddingTop: 20, borderTop: '1px solid rgba(245,242,235,0.06)' }}>
          <a href="/" style={{ fontSize: 13, color: 'rgba(245,242,235,0.3)', textDecoration: 'none' }}>
            Back to Connekted.in
          </a>
        </div>
      </div>
    </div>
  );
}
